const ALIASES = [
  ["subjective", ["subjective", "caregiver report", "presentation", "parent report"]],
  ["objective", ["objective", "behavior", "data", "trial", "prompt", "measur", "program"]],
  ["assessment", ["assessment", "medical necessity", "clinical", "progress", "interpretation", "analysis"]],
  ["plan", ["plan", "next session", "recommendation", "homework"]],
];
const STOP_HEADINGS = /^\s*(OVERALL|WHAT'?S WORKING|AUDIT CHECKLIST|COACHING TIP)\s*:?\s*$/i;
const FIX_HEADING = /^\s*FIX BEFORE SUBMITTING\s*:?\s*$/i;
const PLAIN_SECTION = /^\s*(?:\d+[.)]\s*)?(?:-\s*)?(Subjective|Objective|Assessment|Plan)\b\s*[:\-–—]?/i;

const sectionOf = (label) => {
  const l = label.toLowerCase();
  return ALIASES.find(([, words]) => words.some((w) => l.includes(w)))?.[0] || null;
};

const unquote = (s) => s.trim().replace(/^["“'‘]+|["”'’]+$/g, "").trim();

export function parseCoachFixes(text) {
  const fixes = {};
  let section = null;
  let find = null;
  let blocked = false;
  let pendingFind = null;
  let problem = "";
  text.split("\n").forEach((line, i) => {
    if (pendingFind !== null) {
      const close = line.match(/^(.*?)["”]\s*$/);
      pendingFind += "\n" + (close ? close[1] : line);
      if (close) { find = pendingFind; pendingFind = null; }
      return;
    }
    if (STOP_HEADINGS.test(line)) { blocked = true; section = null; find = null; return; }
    if (FIX_HEADING.test(line)) { blocked = false; section = null; find = null; return; }
    if (blocked) return;
    const b = line.match(/\[([^\]]+)\]/);
    const p = !b && line.match(PLAIN_SECTION);
    if (b || p) {
      section = sectionOf(b ? b[1] : p[1]); find = null;
      problem = (b ? line.slice(line.indexOf(b[0]) + b[0].length) : line.slice(p[0].length)).replace(/^[\s:\-–—]+/, "").trim();
    }
    const r = line.match(/^\s*(?:-\s*)?Replace:\s*(.+)$/i);
    if (r) {
      const raw = r[1].trim();
      if (/^["“]/.test(raw) && !/.["”]$/.test(raw)) pendingFind = raw.replace(/^["“]/, "");
      else find = unquote(raw);
    }
    const t = line.match(/^\s*(?:-\s*)?Try:\s*(.+)$/i);
    if (t && unquote(t[1])) {
      const raw = t[1].trim();
      const replacement = unquote(raw);
      const instruction = !/^["“]/.test(raw) && /^(add|document|include|describe|specify|record|note|consider|clarify|confirm|verify|ensure|list|mention|state|insert)\b/i.test(replacement);
      fixes[i] = { section, find, replacement, instruction, problem, required: /^required for audit/i.test(problem) };
      find = null;
    }
  });
  return fixes;
}

const normChar = (c) => {
  const q = c.replace(/[“”]/g, '"').replace(/[‘’]/g, "'");
  const l = q.toLowerCase();
  return l.length === 1 ? l : q;
};

function locate(haystack, needle) {
  const exact = haystack.indexOf(needle);
  if (exact >= 0) return [exact, needle.length];
  let n = "";
  for (const c of needle.trim()) n += /\s/.test(c) ? (n.endsWith(" ") ? "" : " ") : normChar(c);
  n = n.replace(/[.\s]+$/, "");
  if (!n) return null;
  const map = [];
  let flat = "";
  for (let i = 0; i < haystack.length; i++) {
    const c = haystack[i];
    if (/\s/.test(c)) {
      if (flat.endsWith(" ")) continue;
      flat += " ";
    } else flat += normChar(c);
    map.push(i);
  }
  const at = flat.indexOf(n);
  if (at < 0) return null;
  const start = map[at];
  return [start, map[at + n.length - 1] + 1 - start];
}

export function applyCoachFix(form, { section, find, replacement }) {
  const keys = ["subjective", "objective", "assessment", "plan"];
  const inSection = (k) => k && (form[k] || "").includes(replacement);
  if (find) {
    for (const k of [section, ...keys].filter(Boolean)) {
      const hit = locate(form[k] || "", find);
      if (hit) {
        const [s, len] = hit;
        let end = s + len;
        if (/[.!?]$/.test(replacement) && form[k][end] === ".") end += 1;
        return { key: k, value: form[k].slice(0, s) + replacement + form[k].slice(end), mode: "replaced", original: form[k].slice(s, end), start: s };
      }
    }
    const done = keys.find(inSection);
    return done ? { already: true, key: done } : null;
  }
  if (!section) return null;
  if (inSection(section)) return { already: true, key: section };
  const cur = (form[section] || "").trimEnd();
  return { key: section, value: cur ? `${cur} ${replacement}` : replacement, mode: "added", original: "", start: cur ? cur.length + 1 : 0 };
}

function splice(list, from, to, inserts) {
  const out = [];
  let pos = 0;
  let done = false;
  const put = () => { if (!done) { out.push(...inserts); done = true; } };
  for (const seg of list) {
    if (seg.type === "old") { out.push(seg); continue; }
    const end = pos + seg.text.length;
    const cut = (a, b) => seg.text.slice(Math.max(a, pos) - pos, Math.min(b, end) - pos);
    if (from > pos) { const t = cut(pos, from); if (t) out.push({ ...seg, text: t }); }
    const mid = from < end && to > pos ? cut(from, to) : "";
    if (mid.trim() && seg.type === "plain") out.push({ text: mid, type: "old" });
    if (to <= end) put();
    if (end > to) { const t = cut(to, end); if (t) out.push({ ...seg, text: t }); }
    pos = end;
  }
  put();
  return out;
}

export function previewMany(form, list) {
  const work = { ...form };
  const segs = {};
  const ok = [];
  for (const { id, fix } of list) {
    const r = applyCoachFix(work, fix);
    if (!r || r.already) continue;
    const old = work[r.key] || "";
    const cur = segs[r.key] || [{ text: old, type: "plain" }];
    const from = r.mode === "added" ? old.trimEnd().length : r.start;
    const to = r.mode === "added" ? old.length : r.start + r.original.length;
    const gap = r.mode === "added" ? r.value.slice(from, r.start) : "";
    segs[r.key] = splice(cur, from, to, [...(gap ? [{ text: gap, type: "plain" }] : []), { text: fix.replacement, type: "new", id }]);
    work[r.key] = r.value;
    ok.push(id);
  }
  return { segs, ok };
}

export function undoCoachFix(text, { mode, original }, replacement) {
  const at = text.indexOf(replacement);
  if (at < 0) return null;
  const after = text.slice(at + replacement.length);
  if (mode === "replaced") return text.slice(0, at) + original + after;
  return text.slice(0, at).replace(/ $/, "") + after;
}
